/**
 * Email Connections Helper Functions
 * Handles CRUD operations for email_connections JSONB column in profiles table
 * 
 * This module replaces direct queries to the user_email_connections table with
 * JSONB operations on the profiles.email_connections column.
 * 
 * NOTE: This does NOT handle OAuth tokens - those should remain in a separate
 * secure table or be handled by the existing emailService.js encryption methods.
 */

import { supabase, handleSupabaseError } from '@/lib/supabase';
import type { EmailConnection, NewEmailConnection } from '@/types/database';

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Generates a new UUID for a connection
 * @private
 */
function generateConnectionId(): string {
  return crypto.randomUUID();
}

/**
 * Creates a timestamp string for the current moment
 * @private
 */
function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Validates email address format
 * @private
 */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ============================================
// CORE CRUD OPERATIONS
// ============================================

/**
 * Get all email connections for a user
 * 
 * @param {string} userId - The UUID of the user
 * @returns {Promise<EmailConnection[]>} Array of email connections
 * @throws {Error} If database query fails
 * 
 * @example
 * const connections = await getEmailConnections('user-uuid-here');
 * console.log(`Found ${connections.length} connections`);
 */
export async function getEmailConnections(userId: string): Promise<EmailConnection[]> {
  try {
    // 1. Fetch current data
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('email_connections')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error in getEmailConnections:', fetchError);
      throw new Error(`Failed to fetch email connections: ${fetchError.message}`);
    }

    // 2. Return array (default to empty if null)
    return Array.isArray(profile?.email_connections) ? profile.email_connections : [];
    
  } catch (error) {
    console.error('Error in getEmailConnections:', error);
    throw error;
  }
}

/**
 * Get a specific connection by ID
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection
 * @returns {Promise<EmailConnection | null>} The connection or null if not found
 */
export async function getEmailConnectionById(
  userId: string,
  connectionId: string
): Promise<EmailConnection | null> {
  try {
    const connections = await getEmailConnections(userId);
    return connections.find(connection => connection.id === connectionId) || null;
  } catch (error) {
    console.error('Error in getEmailConnectionById:', error);
    throw error;
  }
}

/**
 * Get connection by email address
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} emailAddress - The email address to search for
 * @returns {Promise<EmailConnection | null>} The connection or null if not found
 */
export async function getEmailConnectionByAddress(
  userId: string,
  emailAddress: string
): Promise<EmailConnection | null> {
  try {
    if (!isValidEmail(emailAddress)) {
      throw new Error('Invalid email address format');
    }

    const connections = await getEmailConnections(userId);
    return connections.find(connection => 
      connection.email_address.toLowerCase() === emailAddress.toLowerCase()
    ) || null;
  } catch (error) {
    console.error('Error in getEmailConnectionByAddress:', error);
    throw error;
  }
}

/**
 * Add a new email connection
 * NOTE: Does NOT store tokens - those must be handled separately
 * 
 * @param {string} userId - The UUID of the user
 * @param {NewEmailConnection} connection - Connection data to add
 * @returns {Promise<EmailConnection>} The created connection with generated ID
 * @throws {Error} If validation fails or database update fails
 */
export async function addEmailConnection(
  userId: string,
  connection: NewEmailConnection
): Promise<EmailConnection> {
  try {
    // 1. Validate
    if (!connection.email_address || !connection.provider) {
      throw new Error('Invalid connection data: missing email_address or provider');
    }

    if (!isValidEmail(connection.email_address)) {
      throw new Error('Invalid email address format');
    }

    // 2. Check if connection already exists
    const existing = await getEmailConnectionByAddress(userId, connection.email_address);
    if (existing) {
      throw new Error(`Email connection for ${connection.email_address} already exists`);
    }

    // 3. Fetch current connections
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('email_connections')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    const currentConnections = Array.isArray(profile?.email_connections) 
      ? profile.email_connections 
      : [];

    // 4. Prepare new connection
    const newConnection: EmailConnection = {
      id: generateConnectionId(),
      email_address: connection.email_address,
      provider: connection.provider,
      last_sync_date: null,
      historical_sync_completed: connection.historical_sync_completed || false,
      sync_enabled: connection.sync_enabled !== false, // Default to true
      created_at: getCurrentTimestamp(),
      updated_at: getCurrentTimestamp()
    };

    // 5. Update database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        email_connections: [...currentConnections, newConnection],
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to add email connection: ${updateError.message}`);
    }

    return newConnection;
    
  } catch (error) {
    console.error('Error in addEmailConnection:', error);
    throw error;
  }
}

/**
 * Update an email connection
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection to update
 * @param {Partial<EmailConnection>} updates - Fields to update
 * @returns {Promise<EmailConnection>} The updated connection
 * @throws {Error} If connection not found or update fails
 */
export async function updateEmailConnection(
  userId: string,
  connectionId: string,
  updates: Partial<EmailConnection>
): Promise<EmailConnection> {
  try {
    // 1. Validate email if being updated
    if (updates.email_address && !isValidEmail(updates.email_address)) {
      throw new Error('Invalid email address format');
    }

    // 2. Fetch current connections
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('email_connections')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    const currentConnections = Array.isArray(profile?.email_connections) 
      ? profile.email_connections 
      : [];

    // 3. Find connection index
    const connectionIndex = currentConnections.findIndex(c => c.id === connectionId);
    if (connectionIndex === -1) {
      throw new Error(`Email connection with ID ${connectionId} not found`);
    }

    // 4. Update the connection
    const updatedConnection: EmailConnection = {
      ...currentConnections[connectionIndex],
      ...updates,
      updated_at: getCurrentTimestamp()
    };

    // 5. Replace in array
    const updatedConnections = [...currentConnections];
    updatedConnections[connectionIndex] = updatedConnection;

    // 6. Save to database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        email_connections: updatedConnections,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to update email connection: ${updateError.message}`);
    }

    return updatedConnection;
    
  } catch (error) {
    console.error('Error in updateEmailConnection:', error);
    throw error;
  }
}

/**
 * Remove an email connection
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection to remove
 * @returns {Promise<void>}
 * @throws {Error} If removal fails
 */
export async function removeEmailConnection(
  userId: string,
  connectionId: string
): Promise<void> {
  try {
    // 1. Fetch current connections
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('email_connections')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    const currentConnections = Array.isArray(profile?.email_connections) 
      ? profile.email_connections 
      : [];

    // 2. Check if connection exists
    const connectionExists = currentConnections.some(c => c.id === connectionId);
    if (!connectionExists) {
      throw new Error(`Email connection with ID ${connectionId} not found`);
    }

    // 3. Remove from array
    const updatedConnections = currentConnections.filter(c => c.id !== connectionId);

    // 4. Save to database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        email_connections: updatedConnections,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to remove email connection: ${updateError.message}`);
    }
    
  } catch (error) {
    console.error('Error in removeEmailConnection:', error);
    throw error;
  }
}

/**
 * Update last sync date (called frequently during email scanning)
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection to update
 * @param {string} syncDate - Optional sync date (defaults to current timestamp)
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export async function updateLastSyncDate(
  userId: string,
  connectionId: string,
  syncDate?: string
): Promise<void> {
  try {
    const syncTimestamp = syncDate || getCurrentTimestamp();
    
    await updateEmailConnection(userId, connectionId, {
      last_sync_date: syncTimestamp
    });
  } catch (error) {
    console.error('Error in updateLastSyncDate:', error);
    throw error;
  }
}

/**
 * Mark historical sync complete
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection to update
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export async function markHistoricalSyncComplete(
  userId: string,
  connectionId: string
): Promise<void> {
  try {
    await updateEmailConnection(userId, connectionId, {
      historical_sync_completed: true
    });
  } catch (error) {
    console.error('Error in markHistoricalSyncComplete:', error);
    throw error;
  }
}

/**
 * Toggle sync enabled
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} connectionId - The UUID of the connection to update
 * @param {boolean} enabled - Whether to enable or disable sync
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export async function toggleSyncEnabled(
  userId: string,
  connectionId: string,
  enabled: boolean
): Promise<void> {
  try {
    await updateEmailConnection(userId, connectionId, {
      sync_enabled: enabled
    });
  } catch (error) {
    console.error('Error in toggleSyncEnabled:', error);
    throw error;
  }
}

/**
 * Get all enabled connections
 * 
 * @param {string} userId - The UUID of the user
 * @returns {Promise<EmailConnection[]>} Array of enabled connections
 */
export async function getEnabledEmailConnections(
  userId: string
): Promise<EmailConnection[]> {
  try {
    const connections = await getEmailConnections(userId);
    return connections.filter(connection => connection.sync_enabled);
  } catch (error) {
    console.error('Error in getEnabledEmailConnections:', error);
    throw error;
  }
}

/**
 * Check if user has any connections
 * 
 * @param {string} userId - The UUID of the user
 * @returns {Promise<boolean>} True if user has any email connections
 */
export async function hasEmailConnections(userId: string): Promise<boolean> {
  try {
    const connections = await getEmailConnections(userId);
    return connections.length > 0;
  } catch (error) {
    console.error('Error in hasEmailConnections:', error);
    throw error;
  }
}